'use client';

import { cva, type VariantProps } from 'class-variance-authority';
import React, {
  createContext,
  forwardRef,
  useContext,
  useState,
  useEffect,
} from 'react';
import { cn } from '../../lib/utils';

// Context for managing tab state
type TabsContextType = {
  activeTab: string;
  setActiveTab: (id: string) => void;
  variant?: 'default' | 'crimson' | 'mint' | 'blue' | 'black';
};

const TabsContext = createContext<TabsContextType | undefined>(
  undefined
);

function useTabsContext() {
  const context = useContext(TabsContext);
  if (!context) {
    throw new Error(
      'Tabs components must be used within a TabsProvider'
    );
  }
  return context;
}

// Tabs container
const tabsVariants = cva(['w-full']);

export interface TabsProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof tabsVariants> {
  defaultTab?: string;
  value?: string;
  onValueChange?: (value: string) => void;
  className?: string;
  children: React.ReactNode;
  variant?: 'default' | 'crimson' | 'mint' | 'blue' | 'black';
}

export const Tabs = forwardRef<HTMLDivElement, TabsProps>(
  function Tabs(
    {
      defaultTab,
      value,
      onValueChange,
      className,
      children,
      variant = 'default',
      ...props
    }: TabsProps,
    ref
  ) {
    const [internalValue, setInternalValue] = useState(
      defaultTab || ''
    );

    // Determine if component is controlled or uncontrolled
    const isControlled = value !== undefined;
    const activeTab = isControlled ? value : internalValue;

    // Update internal state when controlled value changes
    useEffect(() => {
      if (isControlled && value !== internalValue) {
        setInternalValue(value);
      }
    }, [isControlled, value, internalValue]);

    const setActiveTab = (id: string) => {
      if (!isControlled) {
        setInternalValue(id);
      }
      onValueChange?.(id);
    };

    return (
      <TabsContext.Provider
        value={{ activeTab, setActiveTab, variant }}
      >
        <div
          ref={ref}
          className={cn(tabsVariants(), className)}
          {...props}
        >
          {children}
        </div>
      </TabsContext.Provider>
    );
  }
);

// Tab list (container for tab buttons)
const tabListVariants = cva([
  'flex border-b border-border dark:border-border',
]);

export interface TabListProps
  extends React.HTMLAttributes<HTMLDivElement> {
  className?: string;
  children: React.ReactNode;
}

export const TabList = forwardRef<HTMLDivElement, TabListProps>(
  function TabList(
    { className, children, ...props }: TabListProps,
    ref
  ) {
    return (
      <div
        ref={ref}
        role="tablist"
        className={cn(tabListVariants(), className)}
        {...props}
      >
        {children}
      </div>
    );
  }
);

// Tab button
const tabTriggerVariants = cva(
  [
    'inline-flex items-center justify-center whitespace-nowrap px-3 py-2 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
    'border-b-2 border-transparent',
  ],
  {
    variants: {
      variant: {
        default:
          'data-[state=active]:border-primary data-[state=active]:text-foreground',
        crimson:
          'data-[state=active]:border-flame data-[state=active]:text-flame',
        mint: 'data-[state=active]:border-sunlight data-[state=active]:text-sunlight',
        blue: 'data-[state=active]:border-ocean data-[state=active]:text-ocean',
        black:
          'data-[state=active]:border-black data-[state=active]:text-black dark:data-[state=active]:border-white dark:data-[state=active]:text-white',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

export interface TabTriggerProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  id: string;
  className?: string;
  children: React.ReactNode;
}

export const TabTrigger = forwardRef<
  HTMLButtonElement,
  TabTriggerProps
>(function TabTrigger(
  { id, className, children, onClick, ...props }: TabTriggerProps,
  ref
) {
  const { activeTab, setActiveTab, variant } = useTabsContext();
  const isActive = activeTab === id;

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    setActiveTab(id);
    onClick?.(e);
  };

  return (
    <button
      ref={ref}
      role="tab"
      type="button"
      id={`tab-${id}`}
      aria-selected={isActive}
      aria-controls={`tabpanel-${id}`}
      data-state={isActive ? 'active' : 'inactive'}
      className={cn(tabTriggerVariants({ variant }), className)}
      onClick={handleClick}
      {...props}
    >
      {children}
    </button>
  );
});

// Tab content panel
const tabContentVariants = cva([
  'mt-4',
  'data-[state=inactive]:hidden',
]);

export interface TabContentProps
  extends React.HTMLAttributes<HTMLDivElement> {
  id: string;
  className?: string;
  children: React.ReactNode;
}

export const TabContent = forwardRef<HTMLDivElement, TabContentProps>(
  function TabContent(
    { id, className, children, ...props }: TabContentProps,
    ref
  ) {
    const { activeTab } = useTabsContext();
    const isActive = activeTab === id;

    return (
      <div
        ref={ref}
        role="tabpanel"
        id={`tabpanel-${id}`}
        aria-labelledby={`tab-${id}`}
        data-state={isActive ? 'active' : 'inactive'}
        className={cn(tabContentVariants(), className)}
        {...props}
      >
        {children}
      </div>
    );
  }
);

export {
  tabsVariants,
  tabListVariants,
  tabTriggerVariants,
  tabContentVariants,
};
